import type {
  ConfidenceCheckerBriefAnalysis,
  ConfidenceCheckerBriefDeliverable,
  ConfidenceCheckerSummaryEvaluationItem,
} from "@nlc/shared-types";

import { AlertCircle, HelpCircle, Layers, Sparkles } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { Badge, type BadgeVariant } from "@/components/badge";
import { Collapse } from "@/components/collapse";

import { ConfidenceCheckerBriefAnalysisFiles } from "./ConfidenceCheckerBriefAnalysisFiles";

interface ConfidenceCheckerBriefSectionProps {
  briefAnalysis: ConfidenceCheckerBriefAnalysis;
  evaluations: ConfidenceCheckerSummaryEvaluationItem[];
  isLatest?: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onPreview?: (attachmentId: string) => void;
}

export function ConfidenceCheckerBriefSection({
  briefAnalysis,
  evaluations,
  isLatest = false,
  isOpen,
  onOpenChange,
  onPreview,
}: ConfidenceCheckerBriefSectionProps) {
  const {
    briefState,
    clarificationQuestions,
    createdAt,
    deliverables,
    files,
    fileTypes,
    lastParsedAt,
    parseAttempts,
    parseState,
    session,
    sessionId,
    sizes,
    summary,
    unusableReason,
  } = briefAnalysis;

  // Only join the parts that exist, so the subtitle never starts with a stray " · ".
  const dateLabel = lastParsedAt
    ? `Analyzed on ${new Date(lastParsedAt).toLocaleString()}`
    : createdAt
      ? `Created on ${new Date(createdAt).toLocaleString()}`
      : null;
  const filesLabel = sessionId
    ? `${files.length} ${files.length === 1 ? "file" : "files"} evaluated`
    : null;
  const subtitleParts = [
    dateLabel,
    parseAttempts > 1 ? `Attempt ${parseAttempts}` : null,
    getSessionLabel(session),
    filesLabel,
  ].filter((part): part is string => part != null);

  const parseStateBadge = getParseStateBadge(parseState);
  const briefStateBadge = getBriefStateBadge(briefState);

  const header = (
    <div className="flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-brand-secondary text-text-brand-secondary">
        <Sparkles size={20} />
      </div>
      <div className="text-left">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-base font-semibold text-text-primary">
            AI Brief Analysis
          </span>
          {parseStateBadge && (
            <Badge isRounded size="small" variant={parseStateBadge.variant}>
              {parseStateBadge.label}
            </Badge>
          )}
          {briefStateBadge && (
            <Badge isRounded size="small" variant={briefStateBadge.variant}>
              {briefStateBadge.label}
            </Badge>
          )}
          {isLatest && (
            <Badge isRounded size="small" variant="primary">
              Latest
            </Badge>
          )}
        </div>
        <p className="text-xs font-normal text-text-tertiary">
          {subtitleParts.join(" · ")}
        </p>
      </div>
    </div>
  );

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-xl shadow-sm",
        !sessionId && "opacity-70",
      )}
    >
      <Collapse isOpen={isOpen} label={header} onOpenChange={onOpenChange}>
        <div className="flex flex-col gap-5">
          {unusableReason && (
            <div className="flex items-start gap-3 rounded-lg border border-border-error bg-bg-error-secondary p-4">
              <AlertCircle
                className="mt-0.5 shrink-0 text-text-error-primary"
                size={18}
              />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-text-error-primary">
                  Brief Unusable Reason
                </span>
                <span className="text-sm text-text-error-primary">
                  {unusableReason}
                </span>
              </div>
            </div>
          )}

          {summary && (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
                Summary
              </span>
              <p className="text-sm leading-relaxed text-text-secondary whitespace-pre-wrap">
                {summary}
              </p>
            </div>
          )}

          {((sizes && sizes.length > 0) ||
            (fileTypes && fileTypes.length > 0)) && (
            <div className="flex flex-wrap gap-6 pt-1">
              {sizes && sizes.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
                    Expected Dimensions / Sizes
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {sizes.map((size, idx) => (
                      <Badge isFilled key={idx} size="small" variant="grayBlue">
                        {size}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {fileTypes && fileTypes.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
                    Expected File Types
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {fileTypes.map((type, idx) => (
                      <Badge isFilled key={idx} size="small" variant="indigo">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {deliverables && deliverables.length > 0 && (
            <div className="flex flex-col gap-3 pt-2">
              <div className="flex items-center gap-2">
                <Layers className="text-text-quaternary" size={16} />
                <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
                  Deliverables ({deliverables.length})
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {deliverables.map((del, idx) => (
                  <DeliverableItem deliverable={del} key={del.id || idx} />
                ))}
              </div>
            </div>
          )}

          {clarificationQuestions && clarificationQuestions.length > 0 && (
            <div className="flex flex-col gap-2.5 rounded-lg border border-border-warning bg-bg-warning-secondary/40 p-4">
              <div className="flex items-center gap-2">
                <HelpCircle className="text-text-warning-primary" size={16} />
                <span className="text-sm font-semibold text-text-warning-primary">
                  Clarification Questions ({clarificationQuestions.length})
                </span>
              </div>
              <ul className="flex flex-col gap-1.5 pl-5 list-disc text-sm text-text-secondary">
                {clarificationQuestions.map((q, idx) => (
                  <li key={idx}>{q}</li>
                ))}
              </ul>
            </div>
          )}

          {sessionId && (
            <ConfidenceCheckerBriefAnalysisFiles
              evaluations={evaluations}
              files={files}
              onPreview={onPreview}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

function getSessionLabel(
  session: ConfidenceCheckerBriefAnalysis["session"],
): string {
  if (!session) return "Earlier analysis (no upload session)";

  const status =
    session.status.charAt(0).toUpperCase() + session.status.slice(1);

  return `Upload session · ${status}`;
}

function DeliverableItem({
  deliverable,
}: {
  deliverable: ConfidenceCheckerBriefDeliverable;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border-secondary bg-bg-secondary p-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-text-primary">
          {deliverable.name}
        </span>
        {deliverable.count != null && (
          <Badge isRounded size="small" variant="primary">
            Qty: {deliverable.count}
          </Badge>
        )}
      </div>
      {deliverable.description && (
        <p className="text-xs text-text-secondary">{deliverable.description}</p>
      )}
      {deliverable.format && (
        <div className="flex flex-wrap gap-1 pt-1">
          {deliverable.format.sizes?.map((s, idx) => (
            <span
              className="rounded bg-bg-primary px-1.5 py-0.5 text-[11px] font-medium text-text-tertiary border border-border-secondary"
              key={`size-${idx}`}
            >
              {s}
            </span>
          ))}
          {deliverable.format.file_types?.map((t, idx) => (
            <span
              className="rounded bg-bg-primary px-1.5 py-0.5 text-[11px] font-medium text-text-tertiary border border-border-secondary"
              key={`type-${idx}`}
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function getParseStateBadge(
  state: null | string,
): { label: string; variant: BadgeVariant } | null {
  switch (state) {
    case "failed": {
      return { label: "Parse Failed", variant: "error" };
    }
    case "parsing": {
      return { label: "Parsing...", variant: "blue" };
    }
    case "pending": {
      return { label: "Parse Pending", variant: "warning" };
    }
    case "successful": {
      return { label: "Parse Succeeded", variant: "success" };
    }
    default: {
      return null;
    }
  }
}

function getBriefStateBadge(
  state: null | string,
): { label: string; variant: BadgeVariant } | null {
  switch (state) {
    case "ambiguous": {
      return { label: "Brief Ambiguous", variant: "warning" };
    }
    case "clear": {
      return { label: "Brief Clear", variant: "success" };
    }
    case "unusable": {
      return { label: "Brief Unusable", variant: "error" };
    }
    default: {
      return null;
    }
  }
}
